<template>
  <div id="main-container">
    <dir class="title"> {{ $t('sectorViewer.title') }} </dir>
    <v-card class="editor-section">
      <div ref="container" class="konva-container"></div>
      <div class="btn-section">
        <v-btn icon="mdi-minus" @click="zoomWithBtn()"/>
        <v-btn icon="mdi-plus" @click="zoomWithBtn(true)"/>
      </div>
    </v-card>
  </div>
</template>

<script>
import { Stage, Layer, Image, Line, Circle} from 'konva';
import { mapGetters } from 'vuex';

export default {
  name: 'KonvaViewer',
  data() {
    return {
      editorSizes: {
        width: undefined,
        height: undefined,
      },
      sectors: undefined,
      stage: null,
      layer: null,
    }
  },
  mounted() {
    // set editor(stage) size, responsive with screen size
    this.setEditorSizes();

    // define konva stage
    this.stage = new Stage({
      container: this.$refs.container,
      width: this.editorSizes.width,
      height: this.editorSizes.height,
      draggable: true,
      name: 'Stage',
    });

    // create layer
    this.layer = new Layer({});

    // load image for background
    this.setImageBackground();

    // add layer to stage
    this.stage.add(this.layer);

    // event handling for konva stage
    document.addEventListener('mousedown', (e) => { 
      if(e.button === 0) this.stage.draggable(false); 
    });
    document.addEventListener('mouseup', (e) => { 
        if(e.button === 0) this.stage.draggable(true); 
    });
    this.stage.on('wheel', this.zoom);

    // load sectors
    setTimeout(() => {
      this.sectors = this.getSectors;
      this.loadSectors();
    }, 500);
  },
  methods: {
    loadSectors() {
      for (const current of this.sectors) {
        if (current.points) {
          this.drawShapes(current);
        } else {
          this.drawCircle(current)
        }
      }
    },

    // this method load shapes with calculated attributes
    drawShapes(current) {
      var scaleX = this.layer.find(`#image`)[0].attrs.width / current.imageSizes.width;
      var scaleY = this.layer.find(`#image`)[0].attrs.height / current.imageSizes.height;

      const line = new Line({
        points: current.points.map((point, index) => {
          if (index % 2 === 1) {
            return point * scaleX
          } else {
            return point * scaleY
          }
        }),
        scaleX: current.scaleX,
        scaleY: current.scaleY,
        x: current.x ? current.x * scaleX : null,
        y: current.y ? current.y * scaleY : null,
        stroke: 'white',
        strokeWidth: 1,
        closed: true,
        fill: 'rgba(255, 255, 255, 0.1)',
        name: 'Line',
      });

      line.on('mouseover', () => {
        this.stage.container().style.cursor = 'pointer';
        line.setAttrs({
          fill: 'rgba(255, 255, 255, 0.6)',
          cursor: 'pointer'
        })
      })

      line.on('mouseout', () => {
        this.stage.container().style.cursor = 'default';
        line.setAttrs({
          fill: 'rgba(255, 255, 255, 0.1)',
        })
      })
      
      line.on('click', () => {
        this.$emit('selectSector', current.id);
      })

      this.layer.add(line);
      this.layer.draw();
    },

    // this method load shapes with calculated attributes
    drawCircle(current) {
      var scaleX = this.layer.find(`#image`)[0].attrs.width / current.imageSizes.width;
      var scaleY = this.layer.find(`#image`)[0].attrs.height / current.imageSizes.height;
      const line = new Circle({
        radius: current.radius * scaleX,
        x: current.x ? current.x * scaleX : null,
        y: current.y ? current.y * scaleY : null,
        scaleX: current.scaleX,
        scaleY: current.scaleY,
        stroke: 'white',
        strokeWidth: 1,
        closed: true,
        fill: 'rgba(255, 255, 255, 0.1)',
        name: 'Line'
      });

      line.on('mouseover', () => {
        this.stage.container().style.cursor = 'pointer';
        line.setAttrs({
          fill: 'rgba(255, 255, 255, 0.6)',
          cursor: 'pointer'
        })
      })

      line.on('mouseout', () => {
        this.stage.container().style.cursor = 'default';
        line.setAttrs({
          fill: 'rgba(255, 255, 255, 0.1)',
        })
      })

      line.on('click', () => {
        this.$emit('selectSector', current.id);
      })

      this.layer.add(line);
      this.layer.draw();
    },

    // this method set konva editor size, when page mount
    setEditorSizes() {
      const main = document.getElementById('main-container');
      this.editorSizes.width = main.offsetWidth;
      this.editorSizes.height = main.offsetHeight - 110;
      if (window.innerWidth < 700) {
        this.editorSizes.height = main.offsetHeight - 400;
      }
    },

    // this method load image to background or add new image if we select upload option
    setImageBackground() {
      Image.fromURL(`/images/${this.getCurrentImage}`, (image) => {
        const imagePlaceSizeRatioWidth = this.editorSizes.width / image.width();
        const imagePlaceSizeRatioHeight = this.editorSizes.height / image.height();
        const ratio = imagePlaceSizeRatioHeight < imagePlaceSizeRatioWidth ? imagePlaceSizeRatioHeight : imagePlaceSizeRatioWidth;
        image.setAttrs({
          x: 0,
          y: 0,
          height: image.height() * ratio,
          width: image.width() * ratio,
          cornerRadius: 20,
          name: 'Image',
          id: 'image'
        });
        this.layer.add(image);
      });
    },

    // this method hanlde zoom with mouse middle button
    zoom(e) {
      let scaleBy = 1.2;
      e.evt.preventDefault();
      let oldScale = this.stage.scaleX();
      let pointer = this.stage.getRelativePointerPosition();
      let mousePointTo = {
        x: (pointer.x - this.stage.x()) / oldScale,
        y: (pointer.y - this.stage.y()) / oldScale,
      };
      let direction = e.evt.deltaY > 0 ? -1 : 1;
      if (e.evt.ctrlKey) {
        direction = -direction;
      }
      let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
      this.stage.scale({ x: newScale, y: newScale });
      let newPos = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      };
      this.stage.position(newPos);
    },

    // this method hanlde zoom with implemented buttons
    zoomWithBtn(isIn = false) {
      let scaleBy = 1.2;
      let oldScale = this.stage.scaleX();
      let center = {
        x: this.stage.width() / 2,
        y: this.stage.height() / 2,
      };
      let relatedTo = {
        x: (center.x - this.stage.x()) / oldScale,
        y: (center.y - this.stage.y()) / oldScale,
      };
      let newScale = isIn ? oldScale * scaleBy : oldScale / scaleBy;
      this.stage.scale({
        x: newScale,
        y: newScale
      });
      let newPos = {
        x: center.x - relatedTo.x * newScale,
        y: center.y - relatedTo.y * newScale,
      };
      this.stage.position(newPos);
      this.stage.batchDraw();
    },
  },
  computed: {
    ...mapGetters({
      getSectors: 'siteSettings/getSectors',
      getCurrentImage: 'siteSettings/getCurrentImage'
    })
  }
}
</script>

<style lang="scss" scoped>
#main-container {
  height: calc(100% - 20px);
  .title {
    margin-top: 16px;
    margin-bottom: 16px;
    font-size: 22px;
    font-weight: 550;
  }
  .actions {
    display: flex;
    align-items: center;
    margin: 16px 0;
    .sectors {
      max-height: 56px;
      max-width: 160px;
      margin-right: 24px;
    }
    .remove-button {
      margin-right: 24px;
    }
    .cancel-edit-button {
      margin-right: 24px;
    }
    .append-object-section {
      display: flex;
      align-items: center;
      p {
        margin-right: 12px;
      }
      .object-btn {
        margin: 0 4px;
      }
    }
  }
  .editor-section {
    position: relative;
    .konva-container {
    }
    .btn-section {
      position: absolute;
      bottom: 18px;
      right: 18px;
      width: 110px;
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>